import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    Output,
    SimpleChanges,
} from '@angular/core';
import { IDynamicLottieChange } from 'lottie-json-helper/lib/types';
import { buildSingleRow, EditorRow } from './dynamic-row';

/**
 * One typed control for one dynamic-lottie change.
 *
 *   <app-dynamic-value-control
 *       [change]="myChange"
 *       (valueChange)="onChanged($event)">
 *   </app-dynamic-value-control>
 *
 * Picks the right inline control (text chip → input, slick number readout →
 * slider+number input, color swatch → native picker, toggle) based on the
 * change's type/value/range, mirroring the dialog editor's per-row UX.
 *
 * Mutation model: the row holds a private reference to the input `change`
 * object and mutates `change.value` in place on every user input. Callers who
 * pass a shared change reference get the new value reflected back automatically;
 * the `valueChange` output fires AFTER each mutation with the new value (useful
 * for triggering a persist or a re-render without watching the change object).
 */
@Component({
    selector: 'app-dynamic-value-control',
    templateUrl: './dynamic-value-control.component.html',
    styleUrls: ['./dynamic-value-control.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DynamicValueControlComponent implements OnChanges {
    /**
     * The dynamic-lottie change being edited. Required.
     * Identity change rebuilds the internal row; mutating `change.value`
     * externally won't trigger a rebuild — use a new object reference if you
     * need the control to re-derive its display state from outside.
     */
    @Input() change!: IDynamicLottieChange;

    /** Optional override for the resting display "kind" — accepts a prebuilt row
     *  for callers that already classified the change (e.g. the editor list). */
    @Input() row?: EditorRow;

    /** Fires after each user input with the new `change.value`. */
    @Output() valueChange = new EventEmitter<unknown>();

    /** Resolved row — either the externally supplied one or one built here. */
    activeRow!: EditorRow;

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['row'] && this.row) {
            this.activeRow = this.row;
            return;
        }
        if (changes['change'] && this.change) {
            this.activeRow = buildSingleRow(this.change);
        }
    }

    /**
     * Bridge from the row's internal callbacks to the outward (valueChange) emit.
     * Row-mode hosts (e.g. the editor dialog) bind [row] without [change], so
     * `change` is undefined — we optional-chain the payload so the notification
     * still fires (a hard `.value` access would throw and swallow every emit).
     */
    onTextChange(v: string): void {
        if (this.activeRow.kind !== 'text') return;
        this.activeRow.onValueChange(v);
        this.valueChange.emit(this.change?.value);
    }
    onNumberChange(v: string | number): void {
        if (this.activeRow.kind !== 'number') return;
        this.activeRow.onValueChange(v);
        this.valueChange.emit(this.change?.value);
    }
    onHexChange(v: string): void {
        if (this.activeRow.kind === 'color') this.activeRow.onHexChange(v);
        else if (this.activeRow.kind === 'rgb') this.activeRow.onHexChange(v);
        else return;
        this.valueChange.emit(this.change?.value);
    }
    onToggle(v: boolean): void {
        if (this.activeRow.kind !== 'toggle') return;
        this.activeRow.onToggle(v);
        this.valueChange.emit(this.change?.value);
    }
}
